import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/auth'
import { trackAdminLoginPageView } from '../lib/analytics'
import { Lock, Mail, Eye, EyeOff, Shield, Building2 } from 'lucide-react'

function getSubdomain(): string {
  const host = window.location.hostname
  if (host.includes('.ovrwatch.app')) {
    return host.split('.')[0].toLowerCase()
  }
  return ''
}

export default function AdminLogin() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const { adminLogin } = useAuth()

  const subdomain = getSubdomain()
  const isPropertyAdmin = subdomain !== '' && subdomain !== 'admin' && subdomain !== 'www'

  // Track admin login page view on mount
  useEffect(() => { trackAdminLoginPageView() }, [])
  // Capitalize first letter for display
  const propertyName = isPropertyAdmin
    ? subdomain.charAt(0).toUpperCase() + subdomain.slice(1)
    : ''

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      await adminLogin(email, password)
      navigate('/dashboard')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-200 px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        {/* Logo/Header — adapts to admin vs property subdomain */}
        <div className="text-center mb-8">
          <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
            isPropertyAdmin ? 'bg-indigo-600' : 'bg-primary-600'
          }`}>
            {isPropertyAdmin
              ? <Building2 className="w-8 h-8 text-white" />
              : <Shield className="w-8 h-8 text-white" />
            }
          </div>
          {isPropertyAdmin ? (
            <>
              <h1 className="text-2xl font-bold text-gray-900">Property Management</h1>
              <p className="text-indigo-600 font-medium mt-1">{propertyName}</p>
              <p className="text-gray-500 text-sm mt-1">Sign in to manage this property</p>
            </>
          ) : (
            <>
              <h1 className="text-3xl font-bold text-gray-900">Admin Login</h1>
              <p className="text-gray-600 mt-2">OvrWatch Platform</p>
            </>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder={isPropertyAdmin ? 'manager@example.com' : 'admin@example.com'}
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Enter your password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 px-4 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              isPropertyAdmin
                ? 'bg-indigo-600 hover:bg-indigo-700'
                : 'bg-primary-600 hover:bg-primary-700'
            }`}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        {/* Back to Resident Login — only show on property subdomains */}
        {isPropertyAdmin && (
          <div className="mt-6 text-center text-sm text-gray-600">
            <p>
              Resident?{' '}
              <button
                onClick={() => navigate('/login')}
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                Back to Resident Login
              </button>
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
